// Work Plan — the per-day AM/PM support-staff roster. Grid and Timeline layouts over the
// same day, clinic or employee rows, drag & drop assignment from the roster rail,
// copy-from-day, MC review and publish-to-devices. Doctors render read-only from the
// Sessions roster; every write path here excludes them (the server enforces it too).
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  AlertTriangle,
  Bell,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  FileText,
  MapPin,
  RefreshCw,
  Table2,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { appDayKey } from "@/lib/timezone";
import { sessionLabelFor } from "@/lib/work-plan-sessions";
import { buildFlagView, postingFlagIds, resolvePostingFlags } from "@/lib/work-plan-flags";
import { Button } from "@/components/ui/button";
import { Calendar as DatePickerCalendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SegmentedToggle } from "@/components/pages/calendar/facet-filter";
import { useHasPermission } from "@/hooks/use-permissions";
import { useUserPreferences } from "@/hooks/use-user-preferences";
import { setUserPreferences } from "@/lib/stores/user-preference-store";
import { getPendingMCCount, subscribePendingMCCount } from "@/lib/stores/work-plan-mc-store";
import {
  copyWorkPlan,
  createWorkPlanPosting,
  deleteWorkPlanPosting,
  publishWorkPlanDay,
  updateWorkPlanPostingFlags,
  updateWorkPlanPostingTimes,
} from "@/lib/api/work-plan";
import { invalidateWeekLoad, useWeekLoad, useWorkPlanDay } from "./use-work-plan-day";
import {
  buildAppointmentLoad,
  buildDayCoverage,
  buildRoleView,
  clinicIdsSortedByName,
  clinicVisibility,
  copyResultToast,
  dayCoverageFlag,
  formatDayFull,
  formatDayLabel,
  formatDayShort,
  formatLaneRange,
  matchesRolePill,
  moveClinicInOrder,
  moveClinicToIndex,
  orderClinics,
  postingTimes,
  sessionForLaneFraction,
  shiftDate,
  timelineLane,
  toggleClinicHidden,
  weekdayShort,
  workRoleLabel,
} from "./work-plan-utils";
import { IdentityAvatar } from "./planner-bits";
import DayTablePlanner from "./day-table-planner";
import TimelinePlanner from "./timeline-planner";
import RosterRail from "./roster-rail";
import ClinicListControl from "./clinic-list-control";
import CopyPopoverContent from "./copy-popover";
import MCDrawer from "./mc-drawer";

// The Timeline segment's tooltip names the lane, which follows the configured session hours.
function layoutSegments(lane) {
  return [
    { id: "table", label: "Table", icon: Table2, title: "The whole day as a dense name table" },
    { id: "timeline", label: "Timeline", icon: Clock3, title: `True-time ${formatLaneRange(lane)} bars` },
  ];
}
const ROWS_SEGMENTS = [
  { id: "clinic", label: "Clinic", icon: MapPin, title: "One row per clinic" },
  { id: "staff", label: "Employee", icon: Users, title: "One row per employee" },
];
// Roster-rail drag limits — must match the clamp in the preference store, which is what
// actually decides what a restored width can be.
const MIN_RAIL_WIDTH_PX = 200;
const MAX_RAIL_WIDTH_PX = 520;

export default function WorkPlanPage() {
  // Two distinct rights on this page. work_plan:manage owns the DAY-LEVEL acts — publish the
  // day to staff devices, copy a day in, decide MCs. scheduling:manage owns the POSTING
  // mutations the planner grid and roster rail perform (assign / move / remove / time
  // overrides). A planner may hold either one alone, so never collapse these back into one
  // boolean. Being on the page at all already implies work_plan:view.
  const canManagePlan = useHasPermission("work_plan", "manage");
  const canEditPostings = useHasPermission("scheduling", "manage");
  const [date, setDate] = useState(() => appDayKey(new Date()));
  const [layout, setLayout] = useState("table");
  const [mode, setMode] = useState("clinic");
  const [role, setRole] = useState("all");
  const [copyOpen, setCopyOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null);

  const preferences = useUserPreferences();
  // Roster-rail width is a device preference. defaultSize is frozen at mount — it only
  // drives the initial layout, and a changing value would re-register the panel on every
  // drag frame.
  const [initialRailSize] = useState(() => `${preferences.workPlanRailWidthPx}px`);
  const handleRailResize = useCallback((panelSize) => {
    if (panelSize.inPixels > 0) {
      setUserPreferences({ workPlanRailWidthPx: Math.round(panelSize.inPixels) });
    }
  }, []);

  const { data, sessionsByKey, clinicsById, staffById, isLoading, error, refresh, applyLocal } =
    useWorkPlanDay(date);

  const today = appDayKey(new Date());
  const isToday = date === today;

  // The date pill's calendar popover (same picker as the Calendar page). The planner's
  // date is a CIVIL "YYYY-MM-DD" key while react-day-picker speaks local-midnight JS
  // Dates, so both directions convert through plain Y/M/D fields — never through a
  // timestamp, which the app-timezone override would shift across a day boundary.
  const dateAsJsDate = useMemo(() => {
    const [year, month, day] = date.split("-").map(Number);
    return new Date(year, month - 1, day);
  }, [date]);
  const handlePickDate = useCallback((jsDate) => {
    if (!jsDate) return;
    const iso = `${jsDate.getFullYear()}-${String(jsDate.getMonth() + 1).padStart(2, "0")}-${String(jsDate.getDate()).padStart(2, "0")}`;
    setDate(iso);
    setDatePickerOpen(false);
  }, []);

  const [pendingMc, setPendingMc] = useState(() => getPendingMCCount());
  useEffect(() => subscribePendingMCCount(setPendingMc), []);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const sessions = useMemo(() => data?.sessions || [], [data]);
  // The timeline lane follows the configured session hours (08:00–18:00 for the default
  // 08:30–17:30 day); malformed/missing sessions fall back to the 09:00–18:00 lane.
  const lane = useMemo(() => timelineLane(sessions), [sessions]);
  const segments = useMemo(() => layoutSegments(lane), [lane]);
  // The staff-flag vocabulary from the bundle. RAW field on purpose: `supported` is how the
  // planner tells "no flags configured" from "this backend predates flags" (no menu section).
  const flagView = useMemo(() => buildFlagView(data?.flags), [data]);
  const allPostings = useMemo(() => data?.postings || [], [data]);
  const allDoctorSessions = useMemo(() => data?.doctorSessions || [], [data]);
  const workRoles = useMemo(() => data?.workRoles || [], [data]);
  const roleView = useMemo(() => buildRoleView(workRoles), [workRoles]);

  // Which clinics this device shows, and in what order — both device-local preferences.
  // Everything the planner LISTS goes through them (rows, the rail's clinic cards, the
  // pickers, the session counts), while the flags describing where a person actually is
  // (dayCoverage below) stay built from the whole day, so hiding a clinic never turns an
  // "AM → PM Clementi" chip into a lie. Day-level acts (Copy from…, Publish, MC)
  // deliberately ignore both: they cover the real day.
  const orderedClinics = useMemo(
    () => orderClinics(data?.clinics, preferences.workPlanClinicOrder),
    [data, preferences.workPlanClinicOrder],
  );
  const {
    visible: visibleClinics,
    hidden: hiddenClinicIds,
    total: clinicTotal,
    hiddenCount: hiddenClinicCount,
  } = useMemo(
    () => clinicVisibility(orderedClinics, preferences.workPlanHiddenClinicIds),
    [orderedClinics, preferences.workPlanHiddenClinicIds],
  );
  const allClinicsHidden = clinicTotal > 0 && visibleClinics.length === 0;

  const postings = useMemo(
    () => allPostings.filter((posting) => !hiddenClinicIds.has(posting.tenantId)),
    [allPostings, hiddenClinicIds],
  );
  const doctorSessions = useMemo(
    () => allDoctorSessions.filter((item) => !hiddenClinicIds.has(item.tenantId)),
    [allDoctorSessions, hiddenClinicIds],
  );
  const gaps = useMemo(
    () => (data?.gaps || []).filter((gap) => !hiddenClinicIds.has(gap.tenantId)),
    [data, hiddenClinicIds],
  );

  // The control ticks what is SHOWN; the preference stores the hidden complement.
  const toggleClinicVisible = useCallback(
    (clinicId) => {
      setUserPreferences({
        workPlanHiddenClinicIds: toggleClinicHidden(preferences.workPlanHiddenClinicIds, clinicId),
      });
    },
    [preferences.workPlanHiddenClinicIds],
  );
  const showAllClinics = useCallback(() => {
    setUserPreferences({ workPlanHiddenClinicIds: [] });
  }, []);
  const hideAllClinics = useCallback(() => {
    setUserPreferences({ workPlanHiddenClinicIds: orderedClinics.map((clinic) => clinic.id) });
  }, [orderedClinics]);
  // Every reorder writes the WHOLE current order, so the stored list stays a complete
  // snapshot of today's clinics instead of a sparse patch over the server's order.
  const moveClinic = useCallback(
    (clinicId, delta) => {
      setUserPreferences({ workPlanClinicOrder: moveClinicInOrder(orderedClinics, clinicId, delta) });
    },
    [orderedClinics],
  );
  const dropClinicAt = useCallback(
    (clinicId, toIndex) => {
      setUserPreferences({ workPlanClinicOrder: moveClinicToIndex(orderedClinics, clinicId, toIndex) });
    },
    [orderedClinics],
  );
  const sortClinicsByName = useCallback(() => {
    setUserPreferences({ workPlanClinicOrder: clinicIdsSortedByName(orderedClinics) });
  }, [orderedClinics]);
  const resetClinicOrder = useCallback(() => {
    setUserPreferences({ workPlanClinicOrder: [] });
  }, []);

  // The vocabulary is editable in Settings; if the active filter's role was retired,
  // fall back to All rather than filtering the rail to nothing.
  useEffect(() => {
    if (role !== "all" && workRoles.length > 0 && !roleView.byId[role]) {
      setRole("all");
    }
  }, [role, workRoles, roleView]);

  // Who is where across the whole day (support postings + doctor sessions) — the source
  // for the chips' "not here all day" flag.
  // Built from the UNFILTERED day on purpose — see the clinic-visibility note above.
  const dayCoverage = useMemo(
    () => buildDayCoverage(allPostings, allDoctorSessions),
    [allPostings, allDoctorSessions],
  );

  // How busy each clinic session is. Server-counted (see appointmentCountsForDate) — the
  // planner never loads the appointments themselves. Hidden clinics drop out so the session
  // header totals match the rows underneath; a non-array (older backend) passes through
  // untouched so `supported` still reads false.
  const appointmentLoad = useMemo(
    () =>
      buildAppointmentLoad(
        Array.isArray(data?.appointmentCounts)
          ? data.appointmentCounts.filter((item) => !hiddenClinicIds.has(item?.tenantId))
          : data?.appointmentCounts,
      ),
    [data, hiddenClinicIds],
  );

  // Row model shared by both planners: clinics (or employees) × {am, pm} cells holding
  // enriched entries — read-only doctor sessions first, then editable support postings.
  const rows = useMemo(() => {
    if (!data) return [];
    const emptyCells = () => Object.fromEntries(sessions.map((s) => [s.key, { items: [], gaps: [] }]));

    // Only clinic rows need it: an employee row already shows the person's whole day
    // side by side, so a "PM Clementi" pill there would just repeat the next cell.
    const flagFor = (personId, sessionKey, tenantId, displayClinic) =>
      displayClinic
        ? null
        : dayCoverageFlag({
            coverage: dayCoverage,
            personId,
            sessionKey,
            tenantId,
            sessions,
            clinicsById,
          });

    const doctorEntry = (item, displayClinic) => {
      const doctor = staffById[item.doctorId];
      const def = sessionsByKey[item.session];
      const custom = item.start !== def?.start || item.end !== def?.end;
      return {
        key: `doc:${item.blockId}:${item.date}:${item.session}:${item.tenantId}`,
        locked: true,
        mcPending: false,
        personId: item.doctorId,
        clinicId: item.tenantId,
        title: displayClinic
          ? clinicsById[item.tenantId]?.name || "Clinic"
          : doctor?.displayName || "Doctor",
        subtitle: displayClinic ? `${item.start} – ${item.end}` : "Doctor",
        avatarId: displayClinic ? item.tenantId : item.doctorId,
        avatarName: displayClinic
          ? clinicsById[item.tenantId]?.shortName || clinicsById[item.tenantId]?.name
          : doctor?.displayName,
        // Clinics have no photo — an employee row keeps its tinted clinic initials.
        avatarPhotoUrl: displayClinic ? "" : doctor?.avatarUrl || "",
        customTime: custom ? `${item.start}–${item.end}` : "",
        dayFlag: flagFor(item.doctorId, item.session, item.tenantId, displayClinic),
        start: item.start,
        end: item.end,
        posting: null,
      };
    };

    const postingEntry = (posting, displayClinic) => {
      const person = staffById[posting.staffId];
      const clinic = clinicsById[posting.tenantId];
      const times = postingTimes(posting, sessionsByKey);
      const def = sessionsByKey[posting.session];
      return {
        key: `post:${posting.id}`,
        locked: false,
        mcPending: person?.mcStatus === "pending",
        personId: posting.staffId,
        clinicId: posting.tenantId,
        title: displayClinic ? clinic?.name || "Clinic" : person?.displayName || "Staff",
        subtitle: displayClinic ? `${def?.start} – ${def?.end}` : workRoleLabel(person, roleView),
        avatarId: displayClinic ? posting.tenantId : posting.staffId,
        avatarName: displayClinic ? clinic?.shortName || clinic?.name : person?.displayName,
        avatarPhotoUrl: displayClinic ? "" : person?.avatarUrl || "",
        customTime: times.custom ? `${times.start}–${times.end}` : "",
        dayFlag: flagFor(posting.staffId, posting.session, posting.tenantId, displayClinic),
        start: times.start,
        end: times.end,
        // Staff flags: the raw ids (what the toggle menu edits) and the resolved defs in
        // vocabulary order (what the line renders as glyphs; retired ids drop out).
        flagIds: postingFlagIds(posting),
        flags: resolvePostingFlags(posting, flagView),
        posting,
      };
    };

    const gapEntry = (gap) => {
      const who = staffById[gap.staffId];
      return {
        ...gap,
        whoName: who?.displayName || "Someone",
        whoFirstName: (who?.displayName || "Someone").split(/\s+/)[0],
        whoIsDoctor: Boolean(who?.isDoctor),
      };
    };

    if (mode === "clinic") {
      return visibleClinics.map((clinic) => {
        const cells = emptyCells();
        // A clinic row's cell IS a clinic session, so it can carry that session's booked
        // load. Employee rows can't: one cell there may span several clinics.
        if (appointmentLoad.supported) {
          for (const session of sessions) {
            cells[session.key].apptCount = appointmentLoad.byCell[`${clinic.id}|${session.key}`] || 0;
          }
        }
        for (const item of doctorSessions) {
          if (item.tenantId === clinic.id && cells[item.session]) {
            cells[item.session].items.push(doctorEntry(item, false));
          }
        }
        for (const posting of postings) {
          if (posting.tenantId === clinic.id && cells[posting.session]) {
            cells[posting.session].items.push(postingEntry(posting, false));
          }
        }
        for (const gap of gaps) {
          if (gap.tenantId === clinic.id && cells[gap.session]) {
            cells[gap.session].gaps.push(gapEntry(gap));
          }
        }
        return { id: clinic.id, title: clinic.name, sub: clinic.address || clinic.shortName || "", locked: false, cells };
      });
    }

    const people = (data.staff || []).filter(
      (person) => person.isDoctor || matchesRolePill(person, role, roleView),
    );
    const ordered = [...people].sort((a, b) => {
      if (a.isDoctor !== b.isDoctor) return a.isDoctor ? -1 : 1;
      return a.displayName.localeCompare(b.displayName);
    });
    return ordered.map((person) => {
      const cells = emptyCells();
      if (person.isDoctor) {
        for (const item of doctorSessions) {
          if (item.doctorId === person.id && cells[item.session]) {
            cells[item.session].items.push(doctorEntry(item, true));
          }
        }
      } else {
        for (const posting of postings) {
          if (posting.staffId === person.id && cells[posting.session]) {
            cells[posting.session].items.push(postingEntry(posting, true));
          }
        }
      }
      for (const gap of gaps) {
        if (gap.staffId === person.id && cells[gap.session]) {
          cells[gap.session].gaps.push(gapEntry(gap));
        }
      }
      return {
        id: person.id,
        title: person.displayName,
        sub: workRoleLabel(person, roleView),
        photoUrl: person.avatarUrl || "",
        locked: person.isDoctor,
        cells,
      };
    });
  }, [data, mode, role, sessions, sessionsByKey, clinicsById, staffById, roleView, flagView, visibleClinics, postings, gaps, doctorSessions, dayCoverage, appointmentLoad]);

  // The week strip only exists in the Table layout — a null date keeps the hook from
  // fetching anything. Its bars measure ASSIGNED STAFF against the whole available
  // support roster (the rail's list, MC included — being sick doesn't grow the bar), so
  // it works even against a backend that predates appointment counts.
  const weekDays = useWeekLoad(layout === "table" ? date : null, data);
  const supportStaffTotal = useMemo(
    () => (data?.staff || []).filter((person) => !person.isDoctor).length,
    [data],
  );

  const openGapCount = gaps.length;
  const isEmptyDay = postings.length === 0 && gaps.length === 0 && doctorSessions.length === 0;

  // ---- mutations ----

  const assign = useCallback(
    async (tenantId, sessionKey, staffId) => {
      // The drag sensors and Assign buttons are already hidden without the right, but keyboard
      // paths and stale drags can still land here.
      if (!canEditPostings) return;
      const person = staffById[staffId];
      if (person?.isDoctor) {
        toast.error("Doctors are set in Sessions", {
          description: "Doctor sessions come from the recurring Sessions roster and are read-only here.",
        });
        return;
      }
      try {
        const response = await createWorkPlanPosting({ date, tenantId, session: sessionKey, staffId });
        applyLocal((bundle) => ({
          ...bundle,
          postings: [
            ...bundle.postings.filter(
              (p) => !(p.staffId === staffId && p.session === sessionKey) && p.id !== response.posting.id,
            ),
            response.posting,
          ],
          gaps: response.resolvedGapId
            ? bundle.gaps.filter((gap) => gap.id !== response.resolvedGapId)
            : bundle.gaps,
        }));
        if (response.moved) {
          const clinic = clinicsById[tenantId];
          toast("Moved", {
            description: `${person?.displayName || "They"} moved to ${clinic?.name || "the clinic"} for the ${sessionLabelFor(sessions, sessionKey)} session.`,
          });
        }
      } catch (mutationError) {
        toast.error(
          mutationError instanceof Error ? mutationError.message : "Could not assign — try again.",
        );
        refresh();
      }
    },
    [date, sessions, staffById, clinicsById, applyLocal, refresh, canEditPostings],
  );

  const actions = useMemo(
    () => ({
      remove: async (posting) => {
        if (!posting || !canEditPostings) return;
        applyLocal((bundle) => ({
          ...bundle,
          postings: bundle.postings.filter((p) => p.id !== posting.id),
        }));
        try {
          await deleteWorkPlanPosting({ postingId: posting.id });
        } catch (mutationError) {
          toast.error(mutationError instanceof Error ? mutationError.message : "Remove failed.");
          refresh();
        }
      },
      setTimes: async (posting, startTime, endTime) => {
        if (!posting || !canEditPostings) return;
        try {
          const response = await updateWorkPlanPostingTimes({ postingId: posting.id, startTime, endTime });
          applyLocal((bundle) => ({
            ...bundle,
            postings: bundle.postings.map((p) => (p.id === posting.id ? response.posting : p)),
          }));
        } catch (mutationError) {
          toast.error(mutationError instanceof Error ? mutationError.message : "Time override failed.");
        }
      },
      resetTimes: async (posting) => {
        if (!posting || !canEditPostings) return;
        try {
          const response = await updateWorkPlanPostingTimes({ postingId: posting.id, startTime: "", endTime: "" });
          applyLocal((bundle) => ({
            ...bundle,
            postings: bundle.postings.map((p) => (p.id === posting.id ? response.posting : p)),
          }));
        } catch (mutationError) {
          toast.error(mutationError instanceof Error ? mutationError.message : "Reset failed.");
        }
      },
      // Replace a posting's staff flags. Optimistic — the menu stays open while several
      // flags are flipped, so the ticks must follow the click, not the round-trip; the
      // server's posting then lands on top, and an error rolls everything back via refresh.
      setFlags: async (posting, flagIds) => {
        if (!posting || !canEditPostings) return;
        const next = postingFlagIds({ flagIds });
        applyLocal((bundle) => ({
          ...bundle,
          postings: bundle.postings.map((p) => (p.id === posting.id ? { ...p, flagIds: next } : p)),
        }));
        try {
          const response = await updateWorkPlanPostingFlags({ postingId: posting.id, flagIds: next });
          if (response?.posting) {
            applyLocal((bundle) => ({
              ...bundle,
              postings: bundle.postings.map((p) => (p.id === posting.id ? response.posting : p)),
            }));
          }
        } catch (mutationError) {
          toast.error(mutationError instanceof Error ? mutationError.message : "Could not update flags.");
          refresh();
        }
      },
    }),
    [applyLocal, refresh, canEditPostings],
  );

  const handlePickerAssign = useCallback(
    (row, sessionKey, pickedId) => {
      const tenantId = mode === "clinic" ? row.id : pickedId;
      const staffId = mode === "clinic" ? pickedId : row.id;
      void assign(tenantId, sessionKey, staffId);
    },
    [mode, assign],
  );

  const handleDragEnd = useCallback(
    (event) => {
      setActiveDrag(null);
      const { active, over } = event;
      if (!over) return;
      const drag = active.data.current;
      const drop = over.data.current;
      if (!drag || !drop) return;
      let sessionKey = drop.session;
      if (drop.lane) {
        const finalRect = active.rect.current?.translated;
        const laneRect = over.rect;
        const centerX = finalRect ? finalRect.left + finalRect.width / 2 : laneRect.left;
        const fraction = Math.min(1, Math.max(0, (centerX - laneRect.left) / laneRect.width));
        sessionKey = sessionForLaneFraction(fraction, lane);
      }
      if (!sessionKey) return;
      const tenantId = drag.kind === "staff" ? drop.rowId : drag.id;
      const staffId = drag.kind === "staff" ? drag.id : drop.rowId;
      void assign(tenantId, sessionKey, staffId);
    },
    [assign, lane],
  );

  const handleCopy = useCallback(
    async ({ fromDate, scope, tenantId, mode: copyMode, weekly }) => {
      if (!canManagePlan) return;
      setCopyBusy(true);
      try {
        const result = await copyWorkPlan({ fromDate, toDate: date, scope, tenantId, mode: copyMode, weekly });
        setCopyOpen(false);
        toast.success(weekly ? "Week copied" : "Day copied", {
          description: copyResultToast(result, fromDate, weekly),
        });
        // A copy (weekly especially) rewrites postings on days the strip has cached.
        invalidateWeekLoad();
        refresh();
      } catch (copyError) {
        toast.error(copyError instanceof Error ? copyError.message : "Copy failed.");
      } finally {
        setCopyBusy(false);
      }
    },
    [date, refresh, canManagePlan],
  );

  const handlePublish = useCallback(async () => {
    if (!canManagePlan) return;
    setPublishBusy(true);
    try {
      const result = await publishWorkPlanDay({ date });
      const staffNotified = Number(result?.staffNotified) || 0;
      toast.success("Day published", {
        description: `${result?.postings ?? postings.length} posting${(result?.postings ?? postings.length) === 1 ? "" : "s"} sent to ${staffNotified} staff device${staffNotified === 1 ? "" : "s"}.`,
      });
      applyLocal((bundle) => ({ ...bundle, published: true, publishedAt: result?.publishedAt }));
    } catch (publishError) {
      toast.error(publishError instanceof Error ? publishError.message : "Publish failed.");
    } finally {
      setPublishBusy(false);
    }
  }, [date, postings.length, applyLocal, canManagePlan]);

  const Planner = layout === "timeline" ? TimelinePlanner : DayTablePlanner;

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-background text-foreground">
      <header className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-border bg-card/40 px-4">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Calendar</span>
          <span className="text-muted-foreground/60">/</span>
          <span className="font-semibold text-foreground">Work Plan · {formatDayFull(date)}</span>
          {data?.published ? (
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
              Published
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 [&_svg]:size-3.5"
            onClick={refresh}
            title="Refresh"
          >
            <RefreshCw />
          </Button>
          {canManagePlan ? (
            <Popover open={copyOpen} onOpenChange={setCopyOpen}>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" size="sm" className="[&_svg]:size-3.5">
                  <Copy />
                  Copy from
                  <ChevronDown />
                </Button>
              </PopoverTrigger>
              <CopyPopoverContent
                date={date}
                clinics={data?.clinics || []}
                sessions={sessions}
                busy={copyBusy}
                onCopy={handleCopy}
              />
            </Popover>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              "[&_svg]:size-3.5",
              pendingMc > 0 &&
                "border-rose-200 bg-rose-50 font-semibold text-rose-700 hover:bg-rose-100 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300",
            )}
            onClick={() => setDrawerOpen(true)}
            title={
              pendingMc > 0
                ? `${pendingMc} medical certificate${pendingMc === 1 ? "" : "s"} awaiting confirmation`
                : "No medical certificates awaiting confirmation"
            }
          >
            {pendingMc > 0 ? <AlertTriangle /> : <FileText />}
            {pendingMc > 0 ? (
              <>
                Confirm MC
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white">
                  {pendingMc}
                </span>
              </>
            ) : (
              "MC"
            )}
          </Button>
          {canManagePlan ? (
            <Button
              type="button"
              variant="default"
              size="sm"
              className="[&_svg]:size-3.5"
              disabled={publishBusy}
              onClick={handlePublish}
              title="Push this day to staff devices — until published, the app shows no information"
            >
              <Bell />
              Publish day
            </Button>
          ) : null}
        </div>
      </header>

      <div className="flex h-11 shrink-0 items-center gap-2.5 overflow-x-auto border-b border-border bg-muted/20 px-3">
        <SegmentedToggle value={layout} segments={segments} onChange={setLayout} ariaLabel="Layout" size="sm" />
        <SegmentedToggle value={mode} segments={ROWS_SEGMENTS} onChange={setMode} ariaLabel="Rows" size="sm" />
        <span className="h-5 w-px shrink-0 bg-border" />
        <ClinicListControl
          clinics={orderedClinics}
          hiddenIds={hiddenClinicIds}
          orderCustom={preferences.workPlanClinicOrder.length > 0}
          onToggle={toggleClinicVisible}
          onShowAll={showAllClinics}
          onHideAll={hideAllClinics}
          onMove={moveClinic}
          onDropAt={dropClinicAt}
          onSortByName={sortClinicsByName}
          onResetOrder={resetClinicOrder}
        />
        <span className="h-5 w-px shrink-0 bg-border" />
        <div className="flex shrink-0 items-center gap-0.5">
          <Button type="button" variant="ghost" size="icon" className="size-7" onClick={() => setDate(shiftDate(date, -1))} title="Previous day">
            <ChevronLeft className="size-4" />
          </Button>
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                title="Pick a date"
                className="flex h-7 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs transition-colors hover:bg-muted"
              >
                <CalendarDays className="size-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">{weekdayShort(date)}</span>
                <span className="font-semibold tabular-nums">{formatDayShort(date)}</span>
                {isToday ? (
                  <span className="rounded bg-teal-100 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-teal-700 dark:bg-teal-900/50 dark:text-teal-300">
                    Today
                  </span>
                ) : null}
              </button>
            </PopoverTrigger>
            <PopoverContent align="center" className="w-auto p-0">
              <DatePickerCalendar
                mode="single"
                selected={dateAsJsDate}
                defaultMonth={dateAsJsDate}
                onSelect={handlePickDate}
                autoFocus
              />
              <div className="border-t border-border p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-center font-medium"
                  onClick={() => {
                    setDate(today);
                    setDatePickerOpen(false);
                  }}
                >
                  Today
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          <Button type="button" variant="ghost" size="icon" className="size-7" onClick={() => setDate(shiftDate(date, 1))} title="Next day">
            <ChevronRight className="size-4" />
          </Button>
        </div>
        {!isToday ? (
          <Button type="button" variant="outline" size="sm" className="h-7 shrink-0 px-2 text-xs" onClick={() => setDate(today)}>
            Today
          </Button>
        ) : null}
        {/* The one number that demands a decision, promoted out of the footer. */}
        <span
          className={cn(
            "ml-auto shrink-0 text-[11.5px]",
            openGapCount > 0
              ? "font-semibold text-rose-600 dark:text-rose-400"
              : "text-muted-foreground",
          )}
        >
          {openGapCount} needing cover
        </span>
      </div>

      {error ? (
        <div className="border-b border-border bg-destructive/10 px-5 py-2 text-xs text-destructive">{error}</div>
      ) : null}

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={({ active }) => setActiveDrag(active.data.current || null)}
        onDragCancel={() => setActiveDrag(null)}
        onDragEnd={handleDragEnd}
      >
        <ResizablePanelGroup orientation="horizontal" className="min-h-0 flex-1">
          <ResizablePanel className="min-w-0">
          {/* No top padding on the viewport: both planners pin a sticky header (table head /
              hour axis) to top-0, and padding here would let scrolled rows peek out above
              it. The planners pad their own sticky strip instead. */}
          <ScrollArea className="h-full min-w-0 flex-1" viewportClassName="px-4 pb-6">
            {isLoading && !data ? (
              <div className="mt-16 text-center text-sm text-muted-foreground">Loading the day…</div>
            ) : allClinicsHidden ? (
              <div className="mx-auto mt-16 max-w-md rounded-xl border border-dashed border-border px-8 py-9 text-center text-sm text-muted-foreground">
                <p className="mb-1.5 text-[15px] font-semibold text-foreground/80">Every clinic is hidden</p>
                <p className="mb-3">
                  This device is hiding all {clinicTotal} clinic{clinicTotal === 1 ? "" : "s"}, so there is
                  nothing left to plan. Nothing was changed for anyone else.
                </p>
                <Button type="button" variant="outline" size="sm" onClick={showAllClinics}>
                  Show all clinics
                </Button>
              </div>
            ) : isEmptyDay ? (
              <div className="mx-auto mt-16 max-w-md rounded-xl border border-dashed border-border px-8 py-9 text-center text-sm text-muted-foreground">
                <p className="mb-1.5 text-[15px] font-semibold text-foreground/80">
                  Nothing planned for {formatDayLabel(date)}
                </p>
                {/* Three-way: the two rights offer different ways to fill an empty day, and a
                    planner may hold either alone. */}
                {canEditPostings && canManagePlan ? (
                  <p>
                    Drag staff in from the roster, or use <span className="font-semibold">Copy from…</span> to start
                    from {formatDayLabel(shiftDate(date, -1))}.
                  </p>
                ) : canEditPostings ? (
                  <p>Drag staff in from the roster to start building the day.</p>
                ) : canManagePlan ? (
                  <p>
                    Use <span className="font-semibold">Copy from…</span> to start from{" "}
                    {formatDayLabel(shiftDate(date, -1))}.
                  </p>
                ) : (
                  <p>No sessions or postings on this day yet.</p>
                )}
              </div>
            ) : (
              <Planner
                rows={rows}
                sessions={sessions}
                lane={lane}
                mode={mode}
                data={{
                  staff: data?.staff || [],
                  clinics: visibleClinics,
                  roleView,
                  flagDefs: flagView.list,
                  flagsSupported: flagView.supported,
                }}
                appointmentCounts={appointmentLoad.supported ? appointmentLoad.bySession : null}
                canManage={canEditPostings}
                onAssign={handlePickerAssign}
                actions={actions}
                week={layout === "table" && weekDays.length > 0 ? weekDays : null}
                weekCapacity={supportStaffTotal}
                currentDate={date}
                onNavigateDate={setDate}
              />
            )}
          </ScrollArea>
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel
            defaultSize={initialRailSize}
            minSize={`${MIN_RAIL_WIDTH_PX}px`}
            maxSize={`${MAX_RAIL_WIDTH_PX}px`}
            onResize={handleRailResize}
            className="min-w-0"
          >
            <RosterRail
              mode={mode}
              data={data}
              clinics={visibleClinics}
              clinicsById={clinicsById}
              roleView={roleView}
              role={role}
              onRoleChange={setRole}
              postings={postings}
              doctorSessions={doctorSessions}
              sessions={sessions}
              canManage={canEditPostings}
            />
          </ResizablePanel>
        </ResizablePanelGroup>
        <DragOverlay dropAnimation={null} zIndex={60}>
          {activeDrag ? (
            <div className="flex w-52 items-center gap-2 rounded-lg border border-primary/40 bg-card px-2 py-1.5 shadow-lg">
              <IdentityAvatar
                id={activeDrag.id}
                name={
                  activeDrag.kind === "staff"
                    ? staffById[activeDrag.id]?.displayName
                    : clinicsById[activeDrag.id]?.shortName || clinicsById[activeDrag.id]?.name
                }
                photoUrl={activeDrag.kind === "staff" ? staffById[activeDrag.id]?.avatarUrl : ""}
                size={24}
              />
              <span className="truncate text-xs font-semibold">
                {activeDrag.kind === "staff"
                  ? staffById[activeDrag.id]?.displayName
                  : clinicsById[activeDrag.id]?.name}
              </span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <div className="flex shrink-0 items-center justify-between gap-4 border-t border-border bg-muted/30 px-5 py-2 text-[11px] text-muted-foreground">
        <span>
          Doctor sessions come from the Sessions roster (read-only)
          {canEditPostings ? " · drag support staff from the rail to assign" : ""}
        </span>
        <span className="flex items-center gap-4">
          <span>
            {postings.length} support posting{postings.length === 1 ? "" : "s"}
          </span>
          <span>
            {doctorSessions.length} doctor session{doctorSessions.length === 1 ? "" : "s"}
          </span>
          {/* The counts above are what this device SHOWS, so say when they are partial. */}
          {hiddenClinicCount > 0 ? (
            <button
              type="button"
              onClick={showAllClinics}
              title="Show every clinic again on this device"
              className="font-semibold text-primary underline-offset-2 hover:underline"
            >
              {hiddenClinicCount} clinic{hiddenClinicCount === 1 ? "" : "s"} hidden
            </button>
          ) : null}
        </span>
      </div>

      <MCDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        canManage={canManagePlan}
        roleView={roleView}
        sessions={sessions}
        onDecided={refresh}
      />
    </div>
  );
}
